'use client'

import Container from '@/components/structure/Container'
import Title, { type TitleProps } from '@/components/content/common/Title'
import SectionFooter from '@/components/content/home/shared/SectionFooter'

import AppStacksType from '@/components/content/home/sections/types/AppStacksType'
import ComparisonsType from '@/components/content/home/sections/types/ComparisonsType'
import AlternativesType from '@/components/content/home/sections/types/AlternativesType'
import CategoriesType from '@/components/content/home/sections/types/CategoriesType'
import CoursesType from '@/components/content/home/sections/types/CoursesType'

import { Icon } from '@/icons/Icon'
import { cn } from '@/lib/utils'
import type {
  HomeTypesData,
  StackType,
  ComparisonType,
  CategoryType,
  AlternativeType,
  CourseType,
} from '@/app/(app)/fetchers'

type TypeVisuals = TitleProps & {
  visual: React.ReactNode
  stretch: boolean
}

export const getTypes = (
  stack: StackType,
  comparisons: ComparisonType[],
  categories: CategoryType[],
  alternative: AlternativeType | null,
  courses: CourseType[],
): TypeVisuals[] => [
  {
    title: 'App Stacks',
    icon: <Icon name="stack" />,
    paragraphs: ['Stacks for every kind of team and user.', 'Every app earns its place.'],
    buttons: [{ label: 'Explore stacks', link: '/stacks' }],
    type: 'secondary',
    visual: <AppStacksType stack={stack} />,
    stretch: false,
  },
  {
    title: 'Comparisons',
    icon: <Icon name="comparison" />,
    paragraphs: ['Side-by-side views that highlight what really matters when choosing tools.'],
    buttons: [{ label: 'Compare apps', link: '/compare' }],
    type: 'secondary',
    visual: <ComparisonsType comparisons={comparisons} />,
    stretch: false,
  },
  {
    title: 'Best Categories',
    icon: <Icon name="scales" />,
    paragraphs: [
      `Browse by what each app helps you solve.${
        categories.length > 0
          ? ' ' +
            categories
              .slice(0, 4)
              .map((c) => c.name)
              .join(', ') +
            (categories.length > 4 ? ', and more.' : '.')
          : ''
      }`,
    ],
    buttons: [{ label: 'Browse categories', link: '/best' }],
    type: 'secondary',
    visual: categories?.length > 0 && (
      <CategoriesType categories={categories.filter((c) => !!c.slug)} />
    ),
    stretch: true,
  },
  {
    title: 'Alternatives',
    icon: <Icon name="alternative" />,
    paragraphs: ['Find the best option for your use case.', 'Compare, switch, repeat.'],
    buttons: [{ label: 'See alternatives', link: '/alternatives' }],
    type: 'secondary',
    visual: alternative && <AlternativesType key={alternative.name} alternative={alternative} />,
    stretch: false,
  },
  {
    title: 'Courses',
    icon: <Icon name="graduation-cap" />,
    paragraphs: [
      'Learn how to get the most out of your favorite tools. Short, expert-led, useful.',
    ],
    buttons: [
      {
        label: 'Go to the latest course',
        link:
          courses[0]?.app && typeof courses[0].app === 'object' && courses[0].app.slug
            ? `/courses/${courses[0].app.slug}`
            : '/courses',
      },
    ],
    type: 'secondary',
    visual: <CoursesType courses={courses} />,
    stretch: false,
  },
]

export default function HomeTypes({ data }: { data: HomeTypesData }) {
  const { stack, comparisons, categories, alternative, courses } = data

  return (
    <Container>
      <Title
        title="Everything you need for smarter, <span>faster</span> decisions"
        subtitle="Become efficient"
        icon={<Icon name="connect" />}
        paragraphs={[
          'Curated stacks, side-by-side comparisons, and categorized tools. Everything organized for clarity — no noise, just real decisions.',
        ]}
        type="primary"
        className="mb-10"
      />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {stack &&
          getTypes(stack, comparisons, categories, alternative, courses).map((type, index) => (
            <div
              key={index}
              className={cn(
                'relative w-full overflow-hidden rounded-2xl border border-black/10 pt-2.5',
                type.stretch === true ? 'col-span-2' : 'col-span-2 md:col-span-1',
              )}
            >
              <Title {...type} className="relative z-20 px-10 pb-5" />
              {type.visual}
            </div>
          ))}
      </div>

      <SectionFooter
        icon={<Icon name="video" size={12} />}
        text="All resources are produced with care by our editors, <span>Alex</span> and <span>Andra</span>."
        link="/about"
        linkText="Learn how we review"
      />
    </Container>
  )
}
